import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  isByBusOnly,
  isFlight,
  legEndpointToUfoAirport,
  getAircraftTailForFlight,
  priorityServicesAvailable,
} from '@fcom/common-booking/utils/common-booking.utils';
import { BoundType } from '@fcom/dapi';
import { Cabin, FareFamily, FinnairBoundFareFamily, FinnairLocation } from '@fcom/dapi/api/models';
import { NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { deepCopy, stopPropagation, TzDate, unsubscribe } from '@fcom/core/utils';
import { ExtendedFareFamily, FareFamilyBadgeType } from '@fcom/common-booking';
import { Airport, BookingAppState } from '@fcom/common/interfaces/booking';
import { FinnairBoundGroupWithLocation } from '@fcom/common/interfaces/booking/offers.interface';
import { isShortHaulFlight } from '@fcom/common/utils';
import { isLongHaulLegAndAy } from '@fcom/common-booking/utils/flight.utils';
import { finShare } from '@fcom/rx';
import { PseudoLocalActions } from '@fcom/core/actions';
import { GLOBAL_BOOKING_WIDGET_EXPANDER_KEY } from '@fcom/booking-widget/constants';
import { ExpanderStatus } from '@fcom/booking-widget/interfaces';
import { pseudoLocal } from '@fcom/core/selectors';

import { AppConstants } from '../../../../app-constants';
import {
  fareFamiliesBounds,
  inboundsFareFamiliesSortingSelector,
  outboundsFareFamiliesSortingSelector,
  selectedInboundAirBound,
  selectedInboundFareFamilyBenefits,
  selectedOutboundAirBound,
  selectedOutboundFareFamilyBenefits,
} from '../../../../store/selectors';
import { BUS_AIRCRAFT_CODE, ExtendedBoundInfo, TRAIN_AIRCRAFT_CODE } from '../../interfaces';
import { effectiveCabinClass, getPreselectedFareFamily } from '../../utils/offer.utils';
import { BookingAirBoundsService } from '../../services';

export const hasDayChange = ({ details: { arrival, departure } }: FinnairBoundGroupWithLocation): boolean =>
  new TzDate(departure.dateTime).localDateString !== new TzDate(arrival.dateTime).localDateString;

@Component({
  selector: 'fin-air-bounds-bound-selection',
  styleUrls: ['./bound-selection.component.scss'],
  templateUrl: './bound-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AirBoundsBoundSelectionComponent implements OnInit, OnDestroy {
  readonly C: typeof AppConstants = AppConstants;
  readonly IconLibrary = IconLibrary;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  bound$: Observable<ExtendedBoundInfo>;
  @Input()
  boundType: BoundType;
  @Input()
  currencyCode$: Observable<string>;
  @Input()
  index: number;
  @Input()
  control: UntypedFormControl;
  @Input()
  isMobile$: Observable<boolean>;
  @Input()
  viewDetailsBtnKey = 'manageMyBooking.widget.viewFullOrder';
  @Input()
  locations$: Observable<{ [key: string]: FinnairLocation }>;
  @Input()
  showLowestPriceCombo: boolean;

  @Output()
  showDetails: EventEmitter<FinnairBoundGroupWithLocation> = new EventEmitter();
  @Output()
  selectFareFamily: EventEmitter<{
    fareFamily: ExtendedFareFamily;
    checked: boolean;
  }> = new EventEmitter();
  @Output()
  openCabinClassModal: EventEmitter<string> = new EventEmitter();

  hasDayChange$: Observable<boolean>;
  hasBusLeg$: Observable<boolean>;
  hasTrainSegment$: Observable<boolean>;
  priorityServicesAvailable$: Observable<boolean>;
  hasOnlyBusLegs$: Observable<boolean>;
  inputId$: Observable<string>;
  ariaId$: Observable<string>;

  displayPriceAmount$: Observable<string>;
  displayPriceLabel$: Observable<string>;
  ufoAirports$: Observable<{ departure: Airport; arrival: Airport }>;
  tails$: Observable<string[]>;
  campaignBadge$: Observable<string>;
  fareFamilies$: Observable<ExtendedFareFamily[]>;
  isShortHaul$: Observable<boolean>;
  isLongHaulLegAndAy$: Observable<boolean>;
  selectedCabin$ = new BehaviorSubject<Cabin>(null);
  preSelectedFareFamilyId$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  selectedBorderColor$: Observable<string>;
  enableFareFamilyAutoScroll$: Observable<boolean>;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<BookingAppState>,
    private languageService: LanguageService,
    private bookingAirBoundsService: BookingAirBoundsService
  ) {}

  ngOnInit(): void {
    this.hasDayChange$ = this.bound$.pipe(map(hasDayChange));
    this.hasBusLeg$ = this.bound$.pipe(
      map((bound) =>
        bound.details.itinerary.filter(isFlight).some((i) => i.aircraft?.code?.toUpperCase() === BUS_AIRCRAFT_CODE)
      )
    );

    this.hasTrainSegment$ = this.bound$.pipe(
      map((bound) =>
        bound.details.itinerary.filter(isFlight).some((i) => i.aircraft?.code?.toUpperCase() === TRAIN_AIRCRAFT_CODE)
      )
    );

    this.priorityServicesAvailable$ = this.bound$.pipe(
      map((bound) => priorityServicesAvailable(bound.details.itinerary))
    );
    this.hasOnlyBusLegs$ = this.bound$.pipe(map((bound) => isByBusOnly(bound.details)));
    this.inputId$ = this.bound$.pipe(
      map(({ sortingId }: ExtendedBoundInfo): string => `bound-select-input-${sortingId}`)
    );
    this.ariaId$ = this.bound$.pipe(
      map(({ sortingId }: ExtendedBoundInfo): string => `flight-aria-label-${sortingId}`)
    );

    const selectedFareFamilySelector =
      this.boundType === BoundType.outbound ? selectedOutboundAirBound : selectedInboundAirBound;

    this.displayPriceAmount$ = combineLatest([this.bound$, this.store$.pipe(selectedFareFamilySelector())]).pipe(
      map(([{ selected, cheapestPrice }, boundFareFamily]) => (selected ? boundFareFamily?.price : cheapestPrice))
    );

    const selectedFareFamilyBenefitsSelector =
      this.boundType === BoundType.outbound ? selectedOutboundFareFamilyBenefits : selectedInboundFareFamilyBenefits;

    this.displayPriceLabel$ = combineLatest([
      this.bound$,
      this.languageService.translate('startingFromTitle'),
      this.store$.pipe(selectedFareFamilyBenefitsSelector()),
    ]).pipe(
      map(([{ selected }, startingFromTitle, benefits]: [ExtendedBoundInfo, string, FareFamily]) =>
        selected ? benefits?.brandName : startingFromTitle
      )
    );

    this.ufoAirports$ = combineLatest([this.bound$, this.languageService.translate('date')]).pipe(
      map(([bound, translations]) => ({
        departure: legEndpointToUfoAirport(bound.details.departure, translations),
        arrival: legEndpointToUfoAirport(bound.details.arrival, translations),
      }))
    );

    this.tails$ = this.bound$.pipe(
      map((bound) => bound.details.itinerary.filter(isFlight).flatMap((flight) => getAircraftTailForFlight(flight)))
    );

    this.campaignBadge$ = this.bound$.pipe(
      map((bound) => (bound.showSpecialOfferBadge ? 'destinationSearch.campaignPrice' : undefined))
    );

    const fareFamiliesSortingSelector =
      this.boundType === BoundType.outbound
        ? outboundsFareFamiliesSortingSelector
        : inboundsFareFamiliesSortingSelector;

    const fareFamiliesSorting$: Observable<string[]> = this.store$.pipe(fareFamiliesSortingSelector());

    this.fareFamilies$ = combineLatest([
      this.bound$,
      this.store$.pipe(fareFamiliesBounds()),
      this.store$.pipe(selectedFareFamilySelector()),
    ]).pipe(
      map(([bound, fareFamilyMap, selectedFareFamily]) =>
        bound.fareFamilies.map((fareFamily) => {
          const preselectFareFamily = getPreselectedFareFamily(effectiveCabinClass(fareFamily), bound.fareFamilies);
          return {
            ...fareFamily,
            benefits: deepCopy(fareFamilyMap?.[fareFamily.fareFamilyCode]),
            selected: fareFamily.id === selectedFareFamily?.id,
            badgeType: fareFamily.id === preselectFareFamily?.id ? FareFamilyBadgeType.POPULAR : undefined,
          };
        })
      ),
      withLatestFrom(fareFamiliesSorting$),
      map(([fareFamilies, fareFamiliesSorting]) =>
        fareFamilies.sort(
          (a: FinnairBoundFareFamily, b: FinnairBoundFareFamily) =>
            fareFamiliesSorting?.indexOf(a?.fareFamilyCode) - fareFamiliesSorting?.indexOf(b?.fareFamilyCode)
        )
      )
    );

    this.isShortHaul$ = this.bound$.pipe(
      map((bound) =>
        isShortHaulFlight(
          { countryCode: bound.details.departure.countryCode } as FinnairLocation,
          { countryCode: bound.details.arrival.countryCode } as FinnairLocation
        )
      )
    );

    this.isLongHaulLegAndAy$ = combineLatest([this.bound$, this.locations$]).pipe(
      map(([bound, locations]) => isLongHaulLegAndAy(bound.details.itinerary, locations))
    );

    this.selectedBorderColor$ = combineLatest([
      this.fareFamilies$,
      this.store$.pipe(selectedFareFamilySelector()),
    ]).pipe(
      map(
        ([fareFamilies, selectedFareFamily]) =>
          fareFamilies.find((fareFamily) => fareFamily.id === selectedFareFamily?.id)?.benefits?.brandColor ?? null
      )
    );

    this.subscriptions.add(
      this.selectedCabin$
        .pipe(
          filter(Boolean),
          switchMap((cabin) => {
            const selectedFareFamilySelector =
              this.boundType === BoundType.outbound ? selectedOutboundAirBound : selectedInboundAirBound;
            return combineLatest([
              this.bound$,
              this.fareFamilies$,
              this.store$.pipe(selectedFareFamilySelector()),
            ]).pipe(
              map(([bound, fareFamilies, selectedFareFamily]) => {
                if (selectedFareFamily) {
                  return;
                }
                const preselectFareFamily = getPreselectedFareFamily(cabin, bound.fareFamilies);
                return fareFamilies.find((fareFamily) => fareFamily?.id === preselectFareFamily?.id);
              })
            );
          }),
          withLatestFrom(this.bookingAirBoundsService.preselectedBoundId$),
          map(([selectedFareFamily, preselectedBoundId]) => ({
            selectedFareFamily: preselectedBoundId ? null : selectedFareFamily,
            isPreSelected: !!selectedFareFamily && !preselectedBoundId,
          }))
        )
        .subscribe(({ selectedFareFamily, isPreSelected }) => {
          if (selectedFareFamily) {
            this.selectFareFamily.emit({
              fareFamily: selectedFareFamily,
              checked: true,
            });
          }

          if (isPreSelected) {
            this.bookingAirBoundsService.setPreselectedBoundId(selectedFareFamily.id);
          }

          //use for preselecting auto scroll
          this.preSelectedFareFamilyId$.next(isPreSelected ? selectedFareFamily.id : null);
        })
    );

    const isGlobalBookingWidgetClosed$ = this.store$.pipe(
      pseudoLocal(GLOBAL_BOOKING_WIDGET_EXPANDER_KEY),
      map(
        (status) => status === undefined || status === `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${ExpanderStatus.CLOSE}`
      ),
      distinctUntilChanged()
    );

    this.enableFareFamilyAutoScroll$ = combineLatest([
      isGlobalBookingWidgetClosed$,
      this.preSelectedFareFamilyId$,
    ]).pipe(
      map(
        ([isGlobalBookingWidgetClosed, preSelectedFareFamilyId]) =>
          isGlobalBookingWidgetClosed && Boolean(preSelectedFareFamilyId)
      ),
      distinctUntilChanged(),
      finShare()
    );
  }

  ngOnDestroy(): void {
    this.bookingAirBoundsService.resetPreselectedBoundId();
    unsubscribe(this.subscriptions);
  }

  openDetails(e: Event, bound: FinnairBoundGroupWithLocation): void {
    stopPropagation(e);
    this.showDetails.emit(bound);
  }

  handleCabinExpanding(cabin: Cabin): void {
    this.selectedCabin$.next(cabin);
    if (this.preSelectedFareFamilyId$.getValue()) {
      this.store$.dispatch(
        PseudoLocalActions.setValue({
          key: GLOBAL_BOOKING_WIDGET_EXPANDER_KEY,
          data: `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${ExpanderStatus.CLOSE}`,
        })
      );
    }
  }

  cabinAutoScrolled(): void {
    this.preSelectedFareFamilyId$.next(null);
  }
}
